import React, { useState, useEffect } from "react";
import { FaRunning, FaPlus, FaEye } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import DietService from "../../../../Services/DietService";
import { message, Spin } from "antd";
import { FaCirclePlus } from "react-icons/fa6";
import DietPlanHeader from "./DietPlanHeader";
import PatientDietPlanHeader from "./PatientDietPlanHeader";
import DietPlanDay from "./DietPlanDay";
import PatientTracking from "./PatientTracking";

function PatientDietPlan({ appointmentDetails }) {
  const [openPlanModal, setOpenPlanModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previousPlans, setPreviousPlans] = useState([]);
  const [masterPlans, setMasterPlans] = useState([]);
  const [activeTab, setActiveTab] = useState("previous"); // "previous" or "master"
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const [viewingDetails, setViewingDetails] = useState(false);
  const [viewingType, setViewingType] = useState(null); // "master" or "patient"
  const [isEditing, setIsEditing] = useState(false);

  const loggedUser = JSON.parse(sessionStorage.getItem("login_result"));

  useEffect(() => {
    if (appointmentDetails?.patientId) {
      fetchPreviousPlans();
    }
  }, [appointmentDetails?.patientId]);

  const fetchPreviousPlans = async () => {
    setLoading(true);
    try {
      const payload = {
        patientId: appointmentDetails?.patientId,
        status: 1,
      };
      const res = await DietService.getDietList(payload);
      const sortedPlans = (res.data || []).sort((a, b) => {
        const dateA = new Date(a.fromDate);
        const dateB = new Date(b.fromDate);
        if (dateA > dateB) return -1;
        if (dateA < dateB) return 1;

        const toDateA = new Date(a.toDate);
        const toDateB = new Date(b.toDate);
        if (toDateA > toDateB) return -1;
        if (toDateA < toDateB) return 1;
        return 0;
      });
      setPreviousPlans(sortedPlans);
    } catch (error) {
      console.error("Error fetching previous plans:", error);
      message.error("Failed to fetch previous plans");
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterPlans = async () => {
    setLoading(true);
    try {
      const payload = {
        accountId: loggedUser?.user?.account_Id,
        clinicId: loggedUser?.user?.clinic_Id,
        status: 1,
      };
      const res = await DietService.dietPlanHeaderList(payload);
      setMasterPlans(res.data || []);
    } catch (error) {
      console.error("Error fetching master plans:", error);
      message.error("Failed to fetch master plans");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "master" && masterPlans.length === 0) {
      fetchMasterPlans();
    } else if (tab === "previous") {
      fetchPreviousPlans();
    }
  };

  const handleViewPlanDetails = async (planId, type, startEditing = false) => {
    setLoading(true);
    try {
      let res;
      if (type === "master") {
        res = await DietService.dietPlanHeaderGet(planId);
      } else {
        res = await DietService.getDietById(planId);
      }
      const planData = res.data;
      const fromDate = planData.fromDate || new Date().toISOString().split("T")[0];
      const noOfDays = planData.noOfDays || 1;

      const toDate = new Date(fromDate);
      toDate.setDate(toDate.getDate() + (parseInt(noOfDays) - 1));

      setSelectedPlanDetails({
        ...planData,
        fromDate: fromDate,
        toDate: toDate.toISOString().split("T")[0],
        paymentRequiredYN: planData.paymentRequiredYN || "N",
        amount: planData.amount || "",
      });
      setViewingDetails(true);
      setViewingType(type);
      setIsEditing(startEditing);
    } catch (error) {
      console.error("Error fetching plan details:", error);
      message.error("Failed to fetch plan details");
    } finally {
      setLoading(false);
    }
  };

  const formatTime12hr = (timeStr) => {
    if (!timeStr) return "--:--";
    if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;

    const [hoursStr, minutesStr] = timeStr.split(":");
    let hours = parseInt(hoursStr, 10);
    const minutes = minutesStr ? minutesStr.substring(0, 2) : "00";
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const handleCreateCustomPlan = () => {
    const today = new Date().toISOString().split("T")[0];
    const newPlan = {
      chartTitle: "Custom Diet Plan",
      fromDate: today,
      noOfDays: 1,
      toDate: today,
      mealConfirmation: 1,
      imageUploadRequired: 1,
      reminderNotification: 1,
      paymentRequiredYN: "N",
      amount: "",
      dietPlanDetails: [
        {
          dayTitle: "Day 1",
          mealTime: "08:30",
          mealType: "Breakfast",
          meal: "",
          sortOrder: 0,
          activity: "",
          notes: "",
        },
        {
          dayTitle: "Day 1",
          mealTime: "13:30",
          mealType: "Lunch",
          meal: "",
          sortOrder: 1,
          activity: "",
          notes: "",
        },
        {
          dayTitle: "Day 1",
          mealTime: "20:30",
          mealType: "Dinner",
          meal: "",
          sortOrder: 2,
          activity: "",
          notes: "",
        },
      ],
    };
    setSelectedPlanDetails(newPlan);
    setViewingDetails(true);
    setViewingType("custom");
    setIsEditing(true);
  };

  const handleAssignPlan = async (planData) => {
    setLoading(true);
    try {
      let masterData;
      if (typeof planData === "object") {
        masterData = planData;
      } else {
        const res = await DietService.dietPlanHeaderGet(planData);
        masterData = res.data;
      }

      // If it's a custom plan, save it to master first
      if (viewingType === "custom") {
        const masterPayload = {
          chartTitle: masterData.chartTitle,
          createDate: masterData.createDate || new Date().toISOString().split("T")[0],
          noOfDays: masterData.noOfDays,
          planStatus: masterData.planStatus || "Active",
          mealConfirmation: masterData.mealConfirmation || 1,
          imageUploadRequired: masterData.imageUploadRequired || 1,
          reminderNotification: masterData.reminderNotification || 1,
          createdBy: loggedUser?.user?.userId,
          modifiedBy: loggedUser?.user?.userId,
          accountId: loggedUser?.user?.account_Id,
          clinicId: loggedUser?.user?.clinic_Id,
          status: 1,
          paymentRequiredYN: masterData.paymentRequiredYN || "N",
          amount: masterData.paymentRequiredYN === "Y" ? parseFloat(masterData.amount || 0) : 0,
          dietPlanDetails: (
            masterData.patientDietPlanDetails ||
            masterData.dietPlanDetails ||
            []
          ).map((detail) => ({
            dayTitle: detail.dayTitle,
            dayStatus: detail.dayStatus || "Active Plan",
            sortOrder: detail.sortOrder,
            mealTime: detail.mealTime
              ? detail.mealTime.length === 5
                ? `${detail.mealTime}:00`
                : detail.mealTime
              : "00:00:00",
            mealType: detail.mealType,
            meal: detail.meal,
            activity: detail.activity || "",
            notes: detail.notes || "",
          })),
        };

        await DietService.dietPlanHeaderSave(masterPayload);
        fetchMasterPlans();
      }

      const fromDate = masterData.fromDate || new Date().toISOString().split("T")[0];
      const noOfDays = masterData.noOfDays || 1;
      const toDateObj = new Date(fromDate);
      toDateObj.setDate(toDateObj.getDate() + (parseInt(noOfDays) - 1));
      const toDate = toDateObj.toISOString().split("T")[0];

      const payload = {
        userId: loggedUser?.user?.userId,
        patientId: appointmentDetails?.patientId,
        consultationId: appointmentDetails?.consultationId,
        chartTitle: masterData.chartTitle,
        fromDate: fromDate,
        toDate: toDate,
        noOfDays: masterData.noOfDays,
        planStatus: "Active",
        mealConfirmation: masterData.mealConfirmation || 1,
        imageUploadRequired: masterData.imageUploadRequired || 1,
        reminderNotification: masterData.reminderNotification || 1,
        accountId: loggedUser?.user?.account_Id,
        clinicId: loggedUser?.user?.clinic_Id,
        status: 1,
        createdBy: loggedUser?.user?.userId,
        modifiedBy: loggedUser?.user?.userId,
        paymentRequiredYN: masterData.paymentRequiredYN || "N",
        amount: masterData.paymentRequiredYN === "Y" ? parseFloat(masterData.amount || 0) : 0,
        patientDietPlanDetails: (
          masterData.patientDietPlanDetails ||
          masterData.dietPlanDetails ||
          []
        ).map((detail) => ({
          dayTitle: detail.dayTitle,
          dayStatus: detail.dayStatus || "Active Plan",
          sortOrder: detail.sortOrder,
          mealTime: detail.mealTime
            ? detail.mealTime.length === 5
              ? `${detail.mealTime}:00`
              : detail.mealTime
            : "00:00:00",
          mealType: detail.mealType,
          meal: detail.meal,
          activity: detail.activity || "",
          notes: detail.notes || "",
        })),
      };

      await DietService.dietPlanSave(payload);
      if (masterData.paymentRequiredYN === "Y") {
        message.info("The plan will reflect on the patient side after the payment completion");
      } else {
        message.success("Diet plan assigned successfully!");
      }
      setViewingDetails(false);
      setSelectedPlanDetails(null);
      handleTabChange("previous");
    } catch (error) {
      console.error("Error assigning plan:", error);
      message.error("Failed to assign diet plan");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMaster = async () => {
    setLoading(true);
    try {
      const payload = {
        id: selectedPlanDetails.id,
        chartTitle: selectedPlanDetails.chartTitle,
        createDate: selectedPlanDetails.createDate || new Date().toISOString().split("T")[0],
        noOfDays: selectedPlanDetails.noOfDays,
        planStatus: selectedPlanDetails.planStatus || "Active",
        mealConfirmation: selectedPlanDetails.mealConfirmation || 1,
        imageUploadRequired: selectedPlanDetails.imageUploadRequired || 1,
        reminderNotification: selectedPlanDetails.reminderNotification || 1,
        createdBy: selectedPlanDetails.createdBy || loggedUser?.user?.userId,
        modifiedBy: loggedUser?.user?.userId,
        accountId: loggedUser?.user?.account_Id,
        clinicId: loggedUser?.user?.clinic_Id,
        status: 1,
        paymentRequiredYN: selectedPlanDetails.paymentRequiredYN || "N",
        amount: selectedPlanDetails.paymentRequiredYN === "Y" ? parseFloat(selectedPlanDetails.amount || 0) : 0,
        dietPlanDetails: (
          selectedPlanDetails.patientDietPlanDetails ||
          selectedPlanDetails.dietPlanDetails ||
          []
        ).map((detail) => ({
          dayTitle: detail.dayTitle,
          dayStatus: detail.dayStatus || "Active Plan",
          sortOrder: detail.sortOrder,
          mealTime: detail.mealTime
            ? detail.mealTime.length === 5
              ? `${detail.mealTime}:00`
              : detail.mealTime
            : "00:00:00",
          mealType: detail.mealType,
          meal: detail.meal,
          activity: detail.activity || "",
          notes: detail.notes || "",
        })),
      };

      // Remove patient-specific fields if they exist
      delete payload.patientDietPlanDetails;
      delete payload.patientId;
      delete payload.consultationId;

      if (viewingType === "custom") {
        await DietService.dietPlanHeaderSave(payload);
        message.success("Custom plan saved as master template successfully!");
      } else {
        await DietService.dietPlanHeaderUpdate(payload);
        message.success("Master diet plan updated successfully!");
      }

      fetchMasterPlans();
      setViewingDetails(false);
      setIsEditing(false);
      setSelectedPlanDetails(null);
    } catch (error) {
      console.error("Error updating master plan:", error);
      message.error("Failed to update master diet plan");
    } finally {
      setLoading(false);
    }
  };
  const handleUpdatePatientPlan = async () => {
    setLoading(true);
    try {
      const fromDate = selectedPlanDetails.fromDate || new Date().toISOString().split("T")[0];
      const noOfDays = selectedPlanDetails.noOfDays || 1;
      const toDateObj = new Date(fromDate);
      toDateObj.setDate(toDateObj.getDate() + (parseInt(noOfDays) - 1));
      const toDate = toDateObj.toISOString().split("T")[0];

      const payload = {
        ...selectedPlanDetails,
        fromDate: fromDate,
        toDate: toDate,
        paymentRequiredYN: selectedPlanDetails.paymentRequiredYN || "N",
        amount: selectedPlanDetails.paymentRequiredYN === "Y" ? parseFloat(selectedPlanDetails.amount || 0) : 0,
        patientDietPlanDetails: (
          selectedPlanDetails.patientDietPlanDetails ||
          selectedPlanDetails.dietPlanDetails ||
          []
        ).map((detail) => ({
          dayTitle: detail.dayTitle,
          dayStatus: detail.dayStatus,
          sortOrder: detail.sortOrder,
          mealType: detail.mealType,
          meal: detail.meal,
          activity: detail.activity || "",
          notes: detail.notes || "",
          mealTime: detail.mealTime
            ? detail.mealTime.length === 5
              ? `${detail.mealTime}:00`
              : detail.mealTime
            : "00:00:00",
        })),
      };

      // Ensure consistent field names
      delete payload.dietPlanDetails;

      await DietService.dietPlanUpdate(payload);
      if (selectedPlanDetails.paymentRequiredYN === "Y") {
        message.info("The plan will reflect on the patient side after the payment completion");
      } else {
        message.success("Patient diet plan updated successfully!");
      }
      fetchPreviousPlans();
      setViewingDetails(false);
      setIsEditing(false);
      setSelectedPlanDetails(null);
    } catch (error) {
      console.error("Error updating patient plan:", error);
      message.error("Failed to update patient diet plan");
    } finally {
      setLoading(false);
    }
  };

  const handleMealChange = (dayTitle, mealIndex, field, value) => {
    const updatedDetails = { ...selectedPlanDetails };
    const detailsKey = updatedDetails.patientDietPlanDetails
      ? "patientDietPlanDetails"
      : "dietPlanDetails";

    // Find all meals for this day to identify the correct index in the flat list
    const meals = updatedDetails[detailsKey];
    let count = 0;
    let targetIndex = -1;

    for (let i = 0; i < meals.length; i++) {
      if (meals[i].dayTitle === dayTitle) {
        if (count === mealIndex) {
          targetIndex = i;
          break;
        }
        count++;
      }
    }

    if (targetIndex !== -1) {
      updatedDetails[detailsKey][targetIndex] = {
        ...updatedDetails[detailsKey][targetIndex],
        [field]: value,
      };
      setSelectedPlanDetails(updatedDetails);
    }
  };

  const handleActivityChange = (dayTitle, value) => {
    const updatedDetails = { ...selectedPlanDetails };
    const detailsKey = updatedDetails.patientDietPlanDetails
      ? "patientDietPlanDetails"
      : "dietPlanDetails";

    updatedDetails[detailsKey] = updatedDetails[detailsKey].map((meal) => {
      if (meal.dayTitle === dayTitle) {
        return { ...meal, activity: value };
      }
      return meal;
    });
    setSelectedPlanDetails(updatedDetails);
  };

  const handleNotesChange = (dayTitle, value) => {
    const updatedDetails = { ...selectedPlanDetails };
    const detailsKey = updatedDetails.patientDietPlanDetails
      ? "patientDietPlanDetails"
      : "dietPlanDetails";

    updatedDetails[detailsKey] = updatedDetails[detailsKey].map((meal) => {
      if (meal.dayTitle === dayTitle) {
        return { ...meal, notes: value };
      }
      return meal;
    });
    setSelectedPlanDetails(updatedDetails);
  };

  const addMeal = (dayTitle) => {
    const updatedDetails = { ...selectedPlanDetails };
    const detailsKey = updatedDetails.patientDietPlanDetails
      ? "patientDietPlanDetails"
      : "dietPlanDetails";

    const newMeal = {
      dayTitle: dayTitle,
      dayStatus: "Active Plan",
      sortOrder: updatedDetails[detailsKey].filter((m) => m.dayTitle === dayTitle)
        .length,
      mealTime: "08:00",
      mealType: "Breakfast",
      meal: "",
      activity:
        updatedDetails[detailsKey].find((m) => m.dayTitle === dayTitle)
          ?.activity || "",
      notes:
        updatedDetails[detailsKey].find((m) => m.dayTitle === dayTitle)
          ?.notes || "",
    };

    updatedDetails[detailsKey] = [...updatedDetails[detailsKey], newMeal];
    setSelectedPlanDetails(updatedDetails);
  };

  const removeMeal = (dayTitle, mealIndex) => {
    const updatedDetails = { ...selectedPlanDetails };
    const detailsKey = updatedDetails.patientDietPlanDetails
      ? "patientDietPlanDetails"
      : "dietPlanDetails";

    // Find the target index in the flat list
    let count = 0;
    let targetFlatIndex = -1;
    for (let i = 0; i < updatedDetails[detailsKey].length; i++) {
      if (updatedDetails[detailsKey][i].dayTitle === dayTitle) {
        if (count === mealIndex) {
          targetFlatIndex = i;
          break;
        }
        count++;
      }
    }

    if (targetFlatIndex !== -1) {
      updatedDetails[detailsKey].splice(targetFlatIndex, 1);
      setSelectedPlanDetails(updatedDetails);
    }
  };

  const handleHeaderChange = (field, value) => {
    const updatedDetails = { ...selectedPlanDetails, [field]: value };

    // Handle noOfDays or fromDate change for toDate calculation
    if (field === "noOfDays" || field === "fromDate") {
      const fromDate = field === "fromDate" ? value : updatedDetails.fromDate;
      const noOfDays = field === "noOfDays" ? value : updatedDetails.noOfDays;

      if (fromDate && noOfDays) {
        const date = new Date(fromDate);
        date.setDate(date.getDate() + (parseInt(noOfDays) - 1));
        updatedDetails.toDate = date.toISOString().split("T")[0];
      }

      if (field === "noOfDays") {
        const detailsKey = updatedDetails.patientDietPlanDetails
          ? "patientDietPlanDetails"
          : "dietPlanDetails";
        const currentMeals = updatedDetails[detailsKey] || [];
        const targetDays = value || 0;

        // Group current meals by day to see which days we have
        const grouped = currentMeals.reduce((acc, curr) => {
          if (!acc[curr.dayTitle]) acc[curr.dayTitle] = [];
          acc[curr.dayTitle].push(curr);
          return acc;
        }, {});

        const currentDayCount = Object.keys(grouped).length;

        if (targetDays > currentDayCount) {
          // Add new days
          const newMeals = [...currentMeals];
          for (let i = currentDayCount + 1; i <= targetDays; i++) {
            const dayTitle = `Day ${i}`;
            const defaultMeals = [
              {
                dayTitle,
                mealTime: "08:30",
                mealType: "Breakfast",
                meal: "",
                sortOrder: 0,
                activity: "",
                notes: "",
              },
              {
                dayTitle,
                mealTime: "13:30",
                mealType: "Lunch",
                meal: "",
                sortOrder: 1,
                activity: "",
                notes: "",
              },
              {
                dayTitle,
                mealTime: "20:30",
                mealType: "Dinner",
                meal: "",
                sortOrder: 2,
                activity: "",
                notes: "",
              },
            ];
            newMeals.push(...defaultMeals);
          }
          updatedDetails[detailsKey] = newMeals;
        } else if (targetDays < currentDayCount && targetDays >= 1) {
          // Remove days
          const allowedDays = Array.from(
            { length: targetDays },
            (_, i) => `Day ${i + 1}`,
          );
          updatedDetails[detailsKey] = currentMeals.filter((m) =>
            allowedDays.includes(m.dayTitle),
          );
        }
      }
    }

    setSelectedPlanDetails(updatedDetails);
  };

  const copyFromPrevious = (dayTitle) => {
    const dayNumber = parseInt(dayTitle.split(" ")[1]);
    if (dayNumber <= 1) return;

    const previousDayTitle = `Day ${dayNumber - 1}`;
    const updatedDetails = { ...selectedPlanDetails };
    const detailsKey = updatedDetails.patientDietPlanDetails
      ? "patientDietPlanDetails"
      : "dietPlanDetails";

    const previousDayMeals = updatedDetails[detailsKey].filter(
      (m) => m.dayTitle === previousDayTitle,
    );

    if (previousDayMeals.length > 0) {
      // Remove current day meals
      const otherMeals = updatedDetails[detailsKey].filter(
        (m) => m.dayTitle !== dayTitle,
      );

      // Add copied meals
      const copiedMeals = previousDayMeals.map((m) => ({
        ...m,
        dayTitle: dayTitle,
      }));

      updatedDetails[detailsKey] = [...otherMeals, ...copiedMeals];
      setSelectedPlanDetails(updatedDetails);
    }
  };

  const getCurrentPlan = () => {
    if (!previousPlans || previousPlans.length === 0) return null;
    const today = new Date().toISOString().split("T")[0];
    const current = previousPlans.find((plan) => {
      const from = plan.fromDate;
      const to = plan.toDate;
      return from <= today && to >= today;
    });
    return current || previousPlans[0];
  };

  const currentPlan = getCurrentPlan();

  return (
    <div className="flex items-center">
      <div
        onClick={() => setOpenPlanModal(true)}
        className={`p-2 bg-emerald-50 text-emerald-600 rounded-full hover:bg-emerald-100 cursor-pointer transition-colors shadow-sm border border-emerald-100 flex items-center gap-2 ${previousPlans?.length > 0 ? "px-4" : ""}`}
        title="Patient Diet Plan"
      >
        <FaRunning size={20} />
        {currentPlan && (
          <span className="text-xs font-bold text-emerald-700 whitespace-nowrap">
            {currentPlan.chartTitle}
          </span>
        )}
      </div>

      {openPlanModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-[95vw] max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                  <FaRunning size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">
                    Patient Diet Plan
                  </h3>
                  <p className="text-xs text-gray-500">
                    Manage and assign diet plans for{" "}
                    {appointmentDetails?.patientName}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setOpenPlanModal(false);
                  setViewingDetails(false);
                  setSelectedPlanDetails(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
              >
                <MdClose size={24} />
              </button>
            </div>

            {/* Tabs */}
            {!viewingDetails && (
              <div className="flex border-b bg-gray-50/50 px-6">
                <div
                  onClick={() => handleTabChange("previous")}
                  className={`px-6 py-3 text-sm font-semibold transition-all border-b-2 ${activeTab === "previous"
                    ? "border-emerald-500 text-emerald-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                  Previous Plans
                </div>
                <div
                  onClick={() => handleTabChange("master")}
                  className={`px-6 py-3 text-sm font-semibold transition-all border-b-2 ${activeTab === "master"
                    ? "border-emerald-500 text-emerald-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                  Add New Plan
                </div>
                <div
                  onClick={handleCreateCustomPlan}
                  className="ml-auto px-6 py-3 text-sm font-bold text-emerald-600 hover:text-emerald-700 cursor-pointer flex items-center gap-2"
                >
                  <FaPlus size={14} /> New Plan
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30 scrollHide">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Spin size="large" />
                </div>
              ) : viewingDetails ? (
                /* Detail View */
                <div className="space-y-6">
                  <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                    <div>
                      <h4 className="text-xl font-bold text-gray-800">
                        {selectedPlanDetails?.chartTitle}
                      </h4>
                      <div className="flex gap-4 mt-1 text-sm text-gray-500">
                        <span>Days: {selectedPlanDetails?.noOfDays}</span>
                        <span>Status: {selectedPlanDetails?.planStatus}</span>
                        {selectedPlanDetails?.fromDate && (
                          <span>Date: {selectedPlanDetails?.fromDate}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!isEditing && (
                        <button
                          onClick={() => setIsEditing(true)}
                          className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          Edit Plan
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (isEditing) {
                            setIsEditing(false);
                          } else {
                            setViewingDetails(false);
                            setSelectedPlanDetails(null);
                          }
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        {isEditing ? "Cancel Edit" : "Back to List"}
                      </button>
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="space-y-6">
                      {viewingType === "patient" ? (
                        <PatientDietPlanHeader
                          headerData={selectedPlanDetails}
                          onChange={handleHeaderChange}
                        />
                      ) : (
                        <DietPlanHeader
                          headerData={selectedPlanDetails}
                          onChange={handleHeaderChange}
                        />
                      )}

                      <div className="grid grid-cols-1 gap-6">
                        {Object.entries(
                          (
                            selectedPlanDetails?.patientDietPlanDetails ||
                            selectedPlanDetails?.dietPlanDetails ||
                            []
                          )
                            .sort((a, b) => {
                              const dayA = parseInt(a.dayTitle.match(/\d+/)?.[0] || 0);
                              const dayB = parseInt(b.dayTitle.match(/\d+/)?.[0] || 0);
                              if (dayA !== dayB) return dayA - dayB;
                              return (a.sortOrder || 0) - (b.sortOrder || 0);
                            })
                            .reduce((acc, curr) => {
                              if (!acc[curr.dayTitle]) acc[curr.dayTitle] = [];
                              acc[curr.dayTitle].push(curr);
                              return acc;
                            }, {}),
                        ).map(([dayTitle, meals], index) => (
                          <DietPlanDay
                            key={dayTitle}
                            dayTitle={dayTitle}
                            meals={meals}
                            onMealChange={(mealIndex, field, value) =>
                              handleMealChange(dayTitle, mealIndex, field, value)
                            }
                            onAddMeal={() => addMeal(dayTitle)}
                            onRemoveMeal={(mealIndex) =>
                              removeMeal(dayTitle, mealIndex)
                            }
                            onActivityChange={(value) => handleActivityChange(dayTitle, value)}
                            onNotesChange={(value) => handleNotesChange(dayTitle, value)}
                            onCopyPrevious={
                              index > 0 ? () => copyFromPrevious(dayTitle) : null
                            }
                          />
                        ))}
                      </div>

                      <PatientTracking
                        trackingData={selectedPlanDetails}
                        onChange={handleHeaderChange}
                      />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Payment Settings Card */}
                      {(viewingType === "master" || viewingType === "custom") && (
                        <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-5 shadow-sm space-y-4">
                          <h4 className="font-bold text-emerald-800 text-sm flex items-center gap-2">
                            <span className="w-1.5 h-3.5 bg-emerald-500 rounded-full"></span>
                            Payment Settings
                          </h4>
                          <div className="flex flex-wrap items-center gap-6">
                            <label className="flex items-center gap-2.5 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                className="w-4.5 h-4.5 rounded text-emerald-600 focus:ring-emerald-500/30 border-gray-300 transition-all cursor-pointer"
                                checked={selectedPlanDetails?.paymentRequiredYN === "Y"}
                                onChange={(e) => handleHeaderChange("paymentRequiredYN", e.target.checked ? "Y" : "N")}
                              />
                              <span className="text-sm font-semibold text-gray-700">Payment Required from Patient</span>
                            </label>

                            {selectedPlanDetails?.paymentRequiredYN === "Y" && (
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-gray-600">{`${loggedUser?.user?.currencyCode || ""}`}</span>
                                <input
                                  type="number"
                                  min="0"
                                  placeholder="Enter Amount"
                                  className="border rounded-xl px-4 py-1.5 text-sm outline-none focus:border-emerald-500 bg-white w-32 shadow-sm font-semibold transition-all"
                                  value={selectedPlanDetails?.amount || ""}
                                  onChange={(e) => handleHeaderChange("amount", e.target.value)}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 gap-6">
                        {Object.entries(
                          (
                            selectedPlanDetails?.patientDietPlanDetails ||
                            selectedPlanDetails?.dietPlanDetails ||
                            []
                          )
                            .sort((a, b) => {
                              const dayA = parseInt(a.dayTitle.match(/\d+/)?.[0] || 0);
                              const dayB = parseInt(b.dayTitle.match(/\d+/)?.[0] || 0);
                              if (dayA !== dayB) return dayA - dayB;
                              return (a.sortOrder || 0) - (b.sortOrder || 0);
                            })
                            .reduce((acc, curr) => {
                              if (!acc[curr.dayTitle]) acc[curr.dayTitle] = [];
                              acc[curr.dayTitle].push(curr);
                              return acc;
                            }, {}),
                        ).map(([dayTitle, meals], dayIndex) => (
                          <div
                            key={dayIndex}
                            className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                          >
                            <div className="px-5 py-3 bg-emerald-50/50 border-b border-emerald-100">
                              <h5 className="font-bold text-emerald-800">
                                {dayTitle}
                              </h5>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50/50 text-gray-500 uppercase text-[10px] font-bold tracking-wider">
                                  <tr>
                                    <th className="px-5 py-3 text-left">Time</th>
                                    <th className="px-5 py-3 text-left">Type</th>
                                    <th className="px-5 py-3 ">Meal</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {meals.map((meal, idx) => (
                                    <tr
                                      key={idx}
                                      className="hover:bg-gray-50 transition-colors"
                                    >
                                      <td className="px-5 py-4 text-gray-700 font-medium">
                                        {formatTime12hr(meal.mealTime)}
                                      </td>
                                      <td className="px-5 py-4">
                                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-[10px] font-bold">
                                          {meal.mealType}
                                        </span>
                                      </td>
                                      <td className="px-5 py-4">
                                        <span className="px-2 text-start py-1 bg-gray-100 text-gray-600 rounded-md text-[10px] font-bold">
                                          {meal.meal}
                                        </span>
                                      </td>

                                    </tr>
                                  ))}
                                  {meals[0]?.activity && (
                                    <tr className="bg-amber-50/30">
                                      <td className="px-5 py-3 font-bold text-amber-800 text-xs">
                                        Activity
                                      </td>
                                      <td
                                        colSpan="3"
                                        className="px-5 py-3 text-gray-600 text-xs"
                                      >
                                        {meals[0].activity}
                                      </td>
                                    </tr>
                                  )}
                                  {meals[0]?.notes && (
                                    <tr className="bg-blue-50/30">
                                      <td className="px-5 py-3 font-bold text-blue-800 text-xs">
                                        Notes
                                      </td>
                                      <td
                                        colSpan="3"
                                        className="px-5 py-3 text-gray-600 text-xs"
                                      >
                                        {meals[0].notes}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : activeTab === "previous" ? (
                /* Previous Plans List */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {previousPlans.length > 0 ? (
                    previousPlans.map((plan) => (
                      <div
                        key={plan.id}
                        className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div className="p-2 bg-blue-50 text-blue-500 rounded-xl group-hover:scale-110 transition-transform">
                            <FaRunning size={18} />
                          </div>
                          <span
                            className={`text-[10px] font-bold px-2 py-1 rounded-full ${plan.planStatus === "Active"
                              ? "bg-green-100 text-green-600"
                              : "bg-gray-100 text-gray-500"
                              }`}
                          >
                            {plan.planStatus}
                          </span>
                        </div>
                        <h4 className="font-bold text-gray-800 mb-1">
                          {plan.chartTitle}
                        </h4>
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                          <span>{plan.noOfDays} Days</span>
                          <span>{plan.fromDate}</span>
                        </div>
                        <div className="flex gap-2 border-t pt-4">
                          <button
                            onClick={() =>
                              handleViewPlanDetails(plan.id, "patient")
                            }
                            className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors"
                          >
                            <FaEye size={14} /> View Details
                          </button>
                          <button
                            onClick={() =>
                              handleViewPlanDetails(plan.id, "patient", true)
                            }
                            className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                          >
                            <FaPlus size={14} /> Edit
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <FaRunning size={32} />
                      </div>
                      <p className="font-medium">
                        No previous diet plans found
                      </p>
                      <button
                        onClick={() => handleTabChange("master")}
                        className="mt-4 text-emerald-600 font-bold hover:underline bg-white"
                      >
                        Assign from Master Plans
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {masterPlans.length > 0 ? (
                    masterPlans.map((plan) => (
                      <div
                        key={plan.id}
                        className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group"
                      >
                        <h4 className="font-bold text-gray-800 mb-1">
                          {plan.chartTitle}
                        </h4>
                        <div className="flex gap-2 border-t pt-4">
                          <button
                            onClick={() => handleViewPlanDetails(plan.id, "master")}
                            className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                          >
                            <FaEye size={14} /> Preview
                          </button>
                          <button
                            onClick={() => handleViewPlanDetails(plan.id, "master", true)}
                            className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                          >
                            <FaPlus size={14} /> Edit
                          </button>
                          <button
                            onClick={() => handleViewPlanDetails(plan.id, "master")}
                            className="px-6 py-2 text-sm font-bold text-white bg-emerald-500 hover:bg-emerald-600 rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-95"
                          >
                            Assign/Send to Patient
                          </button>

                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400">
                      <p>No master plans available</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="shrink-0 flex justify-end gap-3 px-6 py-4 border-t bg-white">
              <button
                onClick={() => {
                  setOpenPlanModal(false);
                  setViewingDetails(false);
                  setSelectedPlanDetails(null);
                }}
                className="px-6 py-2 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Close
              </button>
              {viewingDetails && viewingType === "patient" && isEditing && (
                <button
                  onClick={handleUpdatePatientPlan}
                  className="px-6 py-2 text-sm font-bold text-white bg-blue-500 hover:bg-blue-600 rounded-xl shadow-lg shadow-blue-500/20 transition-all active:scale-95"
                >
                  Update Patient Plan
                </button>
              )}
              {viewingDetails && (viewingType === "master" || viewingType === "custom") && (
                <>
                  {/* {isEditing && (
                    <button
                      onClick={handleUpdateMaster}
                      className="px-6 py-2 text-sm font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all active:scale-95 border border-blue-100"
                    >
                      Update Master Template
                    </button>
                  )} */}
                  <button
                    onClick={() => handleAssignPlan(selectedPlanDetails)}
                    className="px-6 py-2 text-sm font-bold text-white bg-emerald-500 hover:bg-emerald-600 rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-95"
                  >
                    Assign/Send to Patient
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientDietPlan;
